import React, {useCallback, useEffect, useState} from 'react';
import {useFieldArray, useForm} from 'react-hook-form';
import {connect} from "react-redux";
import {trafficSourceActions} from "../../store/actions/trafficSourceAction";
import {TrafficSourceDialog} from "./TrafficSourceDesign";
import {TrafficSourceForm} from "./TrafficSourceForm";
import {trafficSourceServices} from "../../service/trafficSourceService";
import {useMediaQuery, useTheme} from "@mui/material";
import {customEventServices} from "../../service/customEventService";

function EditTrafficSource(props) {

    const [open, setOpen] = useState(true);
    const [formData, setFormData] = useState(null);
    const [trafficSourceData, setTrafficSourceData] = useState(null);
    const [requestError, setRequestError] = useState("");
    const [loading, setLoading] = useState(false);
    const [formSuccessfullySubmitted, setFormSuccessfullySubmitted] = useState(false);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const {register, handleSubmit, setValue, watch, control, getValues, formState: {errors}} = useForm();
    const {fields, append, remove} = useFieldArray({control, name: "customConversionMappings"});

    const handleClose = useCallback((event, reason) => {
        if (reason === 'backdropClick') {
            return false;
        }
        setOpen(false);
        props.history.push({pathname: "/traffic-sources", state: {from: props.location}});
    }, [props]);

    const onSubmit = (data) => {

        // Add traffic source integration
        if (data?.integrationId?.value) {
            data.integrationId = data?.integrationId?.value;
        }

        // Add workspace id
        data.workspaceId = data?.workspace?.value;
        data.tags = data?.tags ? data?.tags : []

        // modify postbackPercentage int to string
        data.postbackPercentage = data?.postbackPercentage ? data?.postbackPercentage.toString() : ""

        // Map selectedCustomConversions as comma-separated list.
        if (data?.selectedCustomConversions !== "" && data?.selectedCustomConversions !== undefined) {
            let selectedCustomConversion = ""
            // eslint-disable-next-line array-callback-return
            data?.selectedCustomConversions?.map((item) => {
                if (selectedCustomConversion === "") {
                    selectedCustomConversion = item?.id;
                } else {
                    selectedCustomConversion += ", " + item?.id;
                }
            });
            data.selectedCustomConversions = selectedCustomConversion.toString();
        }

        // Reset postback values if usePostback is disabled.
        if (!data?.usePostback) {
            data.postbackUrl = ""
            data.postbackPercentage = "100"
        }

        // Reset data for selectedCustomConversions
        if (!data?.allowPostbackForSelectedCustomConversions) {
            data.selectedCustomConversions = ""
        }

        // Reset data for mappedCustomConversions
        if (!data?.allowCustomConversionMappings) {
            data.customConversionMappings = null;
        }

        // disable allowCustomConversionMappings
        if (data?.allowCustomConversionMappings === true && (data?.customConversionMappings?.length === undefined || data?.customConversionMappings?.length === 0)) {
            data.allowCustomConversionMappings = false
            data.customConversionMappings = null;
        }

        setFormData(data);
    };

    useEffect(() => {
        async function findById(id): void {
            try {
                setRequestError("");
                setLoading(true);
                const response = await trafficSourceServices.find(id);
                if (response.hasOwnProperty("id") && response.id > 0) {

                    // Find selected custom conversions
                    let selectedEvents = response?.selectedCustomConversions?.match(/\d+/g)?.map(Number);
                    if (selectedEvents?.length > 0) {
                        const allEvents = await customEventServices.fetch({});
                        let selectedCustomConversions = [];
                        // eslint-disable-next-line array-callback-return
                        allEvents?.filter(item => {
                            if (selectedEvents.includes(item?.id)) {
                                selectedCustomConversions.push({id: item?.id, name: item?.name});
                            }
                        });
                        response.selectedCustomConversions = selectedCustomConversions;
                    }

                    // Set response
                    setTrafficSourceData(response);
                }
            } catch (error) {
                if (error.hasOwnProperty("response") && error.response.hasOwnProperty("data") && error.response.data.hasOwnProperty("error")) {
                    setRequestError(error.response.data.error);
                }
            } finally {
                setLoading(false);
            }
        }

        if (props.match && props.match.params && props.match.params.hasOwnProperty("id") && trafficSourceData === null) {
            findById(props.match.params.id);
        }

    }, [trafficSourceData, props]);

    useEffect(() => {
        if (trafficSourceData !== null) {
            setTimeout(async () => {
                for (const [key, value] of Object.entries(trafficSourceData)) {
                    if (key !== "integrationId" && key !== "affiliateNetworkIntegrationId") {
                        setValue(key, value);
                    }
                }
            });
        }
    }, [trafficSourceData, setValue]);

    useEffect(() => {
        let id = 0;
        if (props.hasOwnProperty("match") && props.match.hasOwnProperty("params") && props.match.params.hasOwnProperty("id")) {
            id = props.match.params.id;
        }

        async function postData(): void {
            try {
                setRequestError("");
                setLoading(true);
                const response = await trafficSourceServices.update(id, formData);
                if (response.hasOwnProperty("id") && response.id > 0) {
                    props.rerender(true);
                    setFormSuccessfullySubmitted(true);
                }
            } catch (error) {
                if (error.hasOwnProperty("response") && error.response.hasOwnProperty("data") && error.response.data.hasOwnProperty("error")) {
                    setRequestError(error.response.data.error);
                }
            } finally {
                setLoading(false);
            }
        }

        if (formData !== null) {
            postData();
        }

    }, [formData, props]);

    useEffect(() => {
        if (formSuccessfullySubmitted) {
            handleClose();
        }
    }, [formSuccessfullySubmitted, handleClose]);

    return (
        <TrafficSourceDialog open={open}
                onClose={handleClose}
                fullWidth={true}
                fullScreen={fullScreen}
                maxWidth={"md"}
                aria-labelledby="traffic-source-form-title"
        >
            <TrafficSourceForm
                title="Update traffic source"
                register={register}
                handleClose={handleClose}
                requestError={requestError}
                errors={errors}
                handleSubmit={handleSubmit}
                onSubmit={onSubmit}
                loading={loading}
                setValue={setValue}
                watch={watch}
                trafficSourceData={trafficSourceData}
                control={control}
                setRequestError={setRequestError}
                getValues={getValues}
                fields={fields}
                append={append}
                remove={remove}
            />
        </TrafficSourceDialog>
    );
}

const mapDispatchToProps = {
    rerender: trafficSourceActions.rerender
};

export default connect(null, mapDispatchToProps)(EditTrafficSource);